<template>
  <div class="advanced-search">
    <div class="search-body">
      <!-- 条件列表 -->
      <div class="condition-list">
        <div
          v-for="(row, index) in visibleConditions"
          :key="row._id"
          class="condition-row"
        >
          <!-- 逻辑运算符 -->
          <div class="condition-logic">
            <el-select
              v-if="index > 0"
              v-model="row.logic"
              placeholder="逻辑"
              class="logic-select"
            >
              <el-option label="AND" value="AND"></el-option>
              <el-option label="OR" value="OR"></el-option>
              <el-option label="NOT" value="NOT"></el-option>
            </el-select>
            <span v-else class="logic-placeholder"></span>
          </div>

          <!-- 一级下拉：来源（用 label 作为选中值，label 唯一） -->
          <el-select
            v-model="row.sourceLabel"
            placeholder="请选择来源"
            class="condition-select"
            @change="handleSourceChange(row)"
          >
            <el-option
              v-for="opt in sourceOptions"
              :key="opt.label"
              :label="opt.label"
              :value="opt.label"
            ></el-option>
          </el-select>

          <!-- 二级下拉：行为 -->
          <el-select
            v-model="row.behaviorLabel"
            class="condition-select"
            clearable
            @change="handleBehaviorChange(row)"
            style="width: 100px;"
          >
            <el-option
              v-for="opt in behaviorOptions"
              :key="opt.label"
              :label="opt.label"
              :value="opt.label"
            ></el-option>
          </el-select>

          <!-- 三级下拉：特征 -->
          <el-select
            v-model="row.attributeLabel"
            class="condition-select"
            clearable
            @change="handleAttributeChange(row)"
            style="width: 100px;"
          >
            <el-option
              v-for="opt in attributeOptions"
              :key="opt.label"
              :label="opt.label"
              :value="opt.label"
            ></el-option>
          </el-select>

          <!-- 特征值输入 -->
          <template
            v-if="getAttributeCode(row) && getAttributeCode(row) !== 31"
          >
            <!-- 学科(32)：SubjectSelection 组件（单选） -->
            <subject-selection
              v-if="getAttributeCode(row) === 32"
              v-model="row.attributeValue"
              :single="true"
              width="150px"
              class="value-component"
            ></subject-selection>

            <!-- 职称(36)：el-cascader（单选） -->
            <el-cascader
              v-if="getAttributeCode(row) === 36"
              :key="row._id + '-title'"
              v-model="row.attributeValue"
              :options="titleCascaderOptions"
              :props="titleCascaderProps"
              clearable
              :show-all-levels="false"
              filterable
              placeholder="请选择职称"
              class="value-component"
              style="width: 150px;"
            ></el-cascader>

            <!-- 关键词(33)/单位(34)/科室(35)/职务(37)：文本输入 -->
            <el-input
              v-if="
                getAttributeCode(row) !== 32 && getAttributeCode(row) !== 36
              "
              v-model="row.attributeValue"
              :placeholder="getInputPlaceholder(getAttributeCode(row))"
              class="value-input"
              style="width: 150px;"
            ></el-input>
          </template>

          <!-- 操作按钮：+ 或 - -->
          <div class="condition-actions">
            <el-button
              v-if="row.del"
              icon="el-icon-minus"
              class="condition-btn"
              @click="removeRow(index)"
            ></el-button>
            <el-button
              v-if="row.add"
              icon="el-icon-plus"
              class="condition-btn"
              @click="addRow"
            ></el-button>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <el-button type="primary" :loading="loading" @click="handleSearch">
          检索
        </el-button>
        <el-button @click="handleReset">重置</el-button>
      </div>

      <!-- 展开/收起切换 -->
      <div class="toggle-wrapper" v-if="conditionList.length > 1">
        <img
          src="@/assets/img/expand.png"
          class="search-toggle"
          :class="{ 'is-collapsed': !isExpanded }"
          @click="toggleExpand"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SubjectSelection from '@/components/subject/Subject.vue'
import { Get, Post, ProjectConfig } from '@/api'
const increment = ProjectConfig.increment

let rowIdCounter = 0

const SOURCE_OPTIONS = [
  { code: 101, label: '注册用户' },
  { code: 102, label: '发文作者' },
  { code: 103, label: '引证关系作者' },
  { code: 104, label: '引用关系作者' },
  { code: 105, label: '期刊会员' },
  { code: 106, label: '订购用户' },
  { code: 107, label: 'Email Alert订阅用户' },
  { code: 108, label: '白名单' },
  { code: 109, label: '编委' },
  { code: 111, label: '审稿人' },
  { code: 112, label: '投稿人' }
]

const BEHAVIOR_OPTIONS = [
  { code: 22, label: '激活', value: 1 },
  { code: 23, label: '阅读过文章', value: 1 },
  { code: 24, label: '打开率' },
  { code: 22, label: '未激活', value: 0 },
  { code: 23, label: '未阅读过文章', value: 0 }
]

const ATTRIBUTE_OPTIONS = [
  { code: 31, label: '登录过平台', value: 1 },
  { code: 32, label: '学科' },
  { code: 33, label: '关键词' },
  { code: 34, label: '单位' },
  { code: 35, label: '科室' },
  { code: 36, label: '职称' },
  { code: 37, label: '职务' },
  { code: 31, label: '未登录过平台', value: 0 }
]

function createRow() {
  return {
    _id: ++rowIdCounter,
    logic: 'AND',
    sourceLabel: SOURCE_OPTIONS[0].label,
    behaviorLabel: BEHAVIOR_OPTIONS[0].label,
    attributeLabel: ATTRIBUTE_OPTIONS[0].label,
    attributeValue: '',
    add: true,
    del: false
  }
}

export default {
  name: 'AdvancedSearchComponent',
  components: { SubjectSelection },
  props: {
    title: {
      type: String,
      default: '高级检索'
    },
    historyId: {
      type: [String, Number],
      default: ''
    },
    openRateFilter: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      isExpanded: false,
      loading: false,
      conditionList: [createRow()],
      sourceOptions: SOURCE_OPTIONS,
      behaviorOptions: BEHAVIOR_OPTIONS,
      attributeOptions: ATTRIBUTE_OPTIONS,
      // 学科 code->name 映射，用于 API 传值时转换
      subjectNameMap: new Map(),
      // 职称级联选项（结构： [{value, label, children: [{value, label, leaf}]}]）
      titleCascaderOptions: [],
      // 职称 code->name 映射，用于 API 传值时转换
      profTitleNameMap: new Map()
    }
  },
  computed: {
    visibleConditions() {
      if (this.isExpanded) {
        return this.conditionList
      }
      return this.conditionList.slice(0, 1)
    },
    titleCascaderProps() {
      return {
        multiple: false,
        emitPath: true
      }
    }
  },
  created() {
    this.fetchSubjectList()
    this.fetchProfTitleList()
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded
    },
    fetchSubjectList() {
      Get(increment.GetSubject.url, { params: {} })
        .then((res) => {
          if (res && res.data && res.data.data) {
            this.buildSubjectNameMap(res.data.data)
          }
        })
        .catch(() => {})
    },
    buildSubjectNameMap(list) {
      const traverse = (items) => {
        items.forEach((item) => {
          const code = item.code || item.value
          const name = item.name || item.label
          if (code && name) {
            this.subjectNameMap.set(String(code), name)
          }
          if (item.children && item.children.length) {
            traverse(item.children)
          }
        })
      }
      traverse(Array.isArray(list) ? list : [list])
    },
    async fetchProfTitleList() {
      try {
        const res = await Get(increment.GetProfTitleList.url, { params: {} })
        if (res.data.status === 0) {
          const level1Items = res.data.data.map((item) => ({
            value: item.code,
            label: item.name,
            children: []
          }))

          for (const level1 of level1Items) {
            const childRes = await Get(increment.GetProfTitleList.url, {
              params: { code: level1.value }
            })
            if (childRes.data.status === 0) {
              level1.children = childRes.data.data.map((item) => ({
                value: item.code,
                label: item.name,
                leaf: true
              }))
            }
          }

          this.titleCascaderOptions = level1Items
          this.buildProfTitleNameMap(level1Items)
        }
      } catch (error) {
        console.error('获取职称列表失败:', error)
      }
    },
    buildProfTitleNameMap(options) {
      const traverse = (items) => {
        items.forEach((item) => {
          if (item.value && item.label) {
            this.profTitleNameMap.set(String(item.value), item.label)
          }
          if (item.children && item.children.length) {
            traverse(item.children)
          }
        })
      }
      traverse(options || [])
    },
    getInputPlaceholder(code) {
      const map = {
        33: '请输入关键词',
        34: '请输入单位',
        35: '请输入科室',
        37: '请输入职务'
      }
      return map[code] || '请输入'
    },
    handleSourceChange(row) {
      row.behaviorLabel = BEHAVIOR_OPTIONS[0].label
      row.attributeLabel = ATTRIBUTE_OPTIONS[0].label
      row.attributeValue = ''
      this.emitOpenRateChange()
    },
    handleBehaviorChange(row) {
      row.attributeLabel = ATTRIBUTE_OPTIONS[0].label
      row.attributeValue = ''
      this.emitOpenRateChange()
    },
    handleAttributeChange(row) {
      row.attributeValue = ''
    },
    // 根据 label 反查来源 option 的 code（label 唯一，code 可能重复）
    getSourceCode(row) {
      const opt = SOURCE_OPTIONS.find((o) => o.label === row.sourceLabel)
      return opt ? opt.code : null
    },
    // 根据 label 反查行为 option 的 code
    getBehaviorCode(row) {
      const opt = BEHAVIOR_OPTIONS.find((o) => o.label === row.behaviorLabel)
      return opt ? opt.code : null
    },
    // 根据 label 反查行为 option 的 value（如有）
    getBehaviorValue(row) {
      const opt = BEHAVIOR_OPTIONS.find((o) => o.label === row.behaviorLabel)
      return opt && opt.value !== undefined ? opt.value : null
    },
    // 根据 label 反查特征 option 的 code
    getAttributeCode(row) {
      const opt = ATTRIBUTE_OPTIONS.find((o) => o.label === row.attributeLabel)
      return opt ? opt.code : null
    },
    // 根据 label 反查特征 option 的 value（如有）
    getAttributeValue(row) {
      const opt = ATTRIBUTE_OPTIONS.find((o) => o.label === row.attributeLabel)
      return opt && opt.value !== undefined ? opt.value : null
    },
    addRow() {
      this.conditionList.push(createRow())
      this.changeRow()
      this.isExpanded = true
      this.emitOpenRateChange()
    },
    removeRow(index) {
      if (this.conditionList.length > 1) {
        this.conditionList.splice(index, 1)
        this.changeRow()
        this.emitOpenRateChange()
      }
    },
    emitOpenRateChange() {
      const hasOpenRate = this.conditionList.some(
        (row) => this.getBehaviorCode(row) === 24
      )
      this.$emit('open-rate-change', hasOpenRate)
    },
    changeRow() {
      if (this.conditionList.length > 1) {
        this.conditionList.forEach((item, index) => {
          if (index === this.conditionList.length - 1) {
            item.add = true
            item.del = true
          } else {
            item.add = false
            item.del = true
          }
        })
      } else {
        this.conditionList[0].add = true
        this.conditionList[0].del = false
      }
    },
    // 将学科选中值转换为中文名称
    getSubjectNames(row) {
      if (!row.attributeValue || !Array.isArray(row.attributeValue)) return ''
      const names = new Set()
      for (const group of row.attributeValue) {
        if (!Array.isArray(group)) continue
        for (const code of group) {
          const name = this.subjectNameMap.get(String(code))
          if (name) names.add(name)
        }
      }
      return Array.from(names).join('、')
    },
    // 将职称选中值转换为中文名称（单选模式：值为路径数组 [code1, code2]）
    getProfTitleNames(row) {
      if (!row.attributeValue || !Array.isArray(row.attributeValue)) return ''
      const names = []
      for (const code of row.attributeValue) {
        const name = this.profTitleNameMap.get(String(code))
        if (name) names.push(name)
      }
      return names.join('、')
    },
    // 获取特征值的中文名称，用于 API 传参
    getAttributeDisplayName(row) {
      const code = this.getAttributeCode(row)
      if (!code) return ''
      if (code === 32) {
        // 学科：单选 SubjectSelection，值为单个 code
        const subjectCode = String(row.attributeValue || '')
        return this.subjectNameMap.get(subjectCode) || subjectCode
      }
      if (code === 36) {
        // 职称：单选 el-cascader，值为路径数组 [code1, code2]
        return this.getProfTitleNames(row)
      }
      // 关键词/单位/科室/职务：直接使用输入的文本
      return String(row.attributeValue || '')
    },
    // 验证特征值是否为空
    isAttributeValueEmpty(row) {
      if (!row.attributeValue) return true
      if (this.getAttributeCode(row) === 36) {
        return (
          !Array.isArray(row.attributeValue) || row.attributeValue.length === 0
        )
      }
      // 学科(32)为单选字符串，职务(37)为文本，其他文本类同样判断
      return !String(row.attributeValue).trim()
    },
    validateConditions() {
      for (const row of this.conditionList) {
        if (!row.sourceLabel) continue
        const attrCode = this.getAttributeCode(row)
        if (attrCode && attrCode !== 31) {
          if (this.isAttributeValueEmpty(row)) {
            const attrOption = ATTRIBUTE_OPTIONS.find(
              (opt) => opt.label === row.attributeLabel
            )
            const attrLabel = attrOption ? attrOption.label : '该特征'
            return `第${this.conditionList.indexOf(row) +
              1}行：${attrLabel}检索值不能为空`
          }
        }
        if (this.getBehaviorCode(row) === 24 && !this.openRateFilter) {
          return '打开率筛选值不能为空'
        }
      }
      return ''
    },
    buildSearchParams() {
      const rows = this.conditionList
        .filter((row) => row.sourceLabel)
        .map((row) => {
          const sourceCode = this.getSourceCode(row)
          const rowData = {
            sourceCode
          }
          // 如果来源 option 对应有 value，也传递上
          const sourceOpt = SOURCE_OPTIONS.find(
            (o) => o.label === row.sourceLabel
          )
          if (sourceOpt && sourceOpt.value !== undefined) {
            rowData.sourceValue = sourceOpt.value
          }
          if (row.behaviorLabel) {
            const behaviorCode = this.getBehaviorCode(row)
            const behavior = {
              behaviorCode
            }
            // 打开率需要附带筛选值
            if (behaviorCode === 24 && this.openRateFilter) {
              behavior.operator = 'GE'
              behavior.value = String(this.openRateFilter)
            }
            // 如果行为 option 对应有 value，以 value 传递
            const behaviorValue = this.getBehaviorValue(row)
            if (behaviorValue !== null) {
              behavior.value = behaviorValue
            }
            rowData.behavior = behavior
          }
          if (row.attributeLabel) {
            const attributeCode = this.getAttributeCode(row)
            const attribute = {
              attributeCode
            }
            // 如果特征 option 对应有 value，以 value 传递
            const attributeValue = this.getAttributeValue(row)
            if (attributeValue !== null) {
              attribute.value = attributeValue
            } else if (attributeCode !== 31) {
              // 非登录/未登录且无内置 value，使用输入的中文名称
              attribute.value = this.getAttributeDisplayName(row)
            }
            rowData.attribute = attribute
          }
          if (row.logic) {
            rowData.logic = row.logic
          }
          return rowData
        })

      return {
        historyId: Number(this.historyId) || this.historyId,
        group: {
          name: this.title,
          rows
        }
      }
    },
    async handleSearch() {
      const hasFilled = this.conditionList.some((row) => row.sourceLabel)
      if (!hasFilled) {
        this.$message.warning('请至少填写一个条件')
        return
      }

      const errorMsg = this.validateConditions()
      if (errorMsg) {
        this.$message.error(errorMsg)
        return
      }

      this.loading = true
      const params = this.buildSearchParams()

      try {
        const res = await Post(increment.GetABTestCount.url, params)
        if (res.data.status === 0) {
          this.$emit('search-success', res, params)
        } else {
          this.$message.error(res.data.message || '检索失败')
        }
      } catch (err) {
        this.$message.error('检索失败，请重试')
        this.$emit('search-error', err)
      } finally {
        this.loading = false
      }
    },
    handleReset() {
      this.conditionList = [createRow()]
      this.isExpanded = false
    }
  }
}
</script>

<style lang="scss" scoped>
.advanced-search {
  width: 100%;

  .search-body {
    padding: 16px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 4px;
  }

  .condition-list {
    .condition-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      flex-wrap: nowrap;
      overflow-x: auto;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .condition-logic {
      flex-shrink: 0;

      .logic-select {
        width: 80px;
      }
      .logic-placeholder {
        display: inline-block;
        width: 80px;
        height: 32px;
      }
    }

    .condition-select {
      width: 130px;
      flex-shrink: 0;
    }

    .value-component {
      width: 150px;
      flex-shrink: 0;
    }

    .value-input {
      width: 150px;
      flex-shrink: 0;
    }

    .condition-actions {
      display: flex;
      gap: 6px;
      flex-shrink: 0;

      .condition-btn {
        margin-left: 0;
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 12px;
    margin-top: 16px;
    justify-content: center;
  }

  .toggle-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 12px;

    .search-toggle {
      width: 24px;
      cursor: pointer;
      transition: transform 0.3s;

      &.is-collapsed {
        transform: rotate(-180deg);
      }

      &:hover {
        opacity: 0.7;
      }
    }
  }
}
</style>
